import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectDarkMode } from "../../../../app/store/slices/uiSlice";
import vaxicarefull from "../../../../assets/images/VaxiCare-full.png";
import vaxicaresmall from "../../../../assets/images/VaxiCare-small.png";
import { HomeIcon, BeakerIcon, MapPinIcon, CalendarIcon, UserGroupIcon, UsersIcon, ShieldCheckIcon, DocumentChartBarIcon, Cog6ToothIcon, XMarkIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import { useRbac } from "../../../hooks/usePermission";
import { canAccessModule } from "../../../utils/rbac";

// Menu configuration with explicit Tailwind classes for proper JIT compilation
const menuItems = [
  {
    section: "dashboard",
    label: "Dashboard",
    icon: HomeIcon,
    path: "/admin/dashboard",
    iconBg: "bg-gradient-to-br from-blue-500 to-blue-600",
    activeBg: "bg-blue-100 dark:bg-blue-900/20",
    activeText: "text-blue-700 dark:text-blue-400",
    hoverBg: "hover:bg-blue-50 dark:hover:bg-blue-900/10",
    dotBg: "bg-blue-500",
    indicatorBg: "bg-gradient-to-b from-blue-500 to-blue-600",
  },
  {
    section: "vaccine-management",
    label: "Vaccine Management",
    icon: BeakerIcon,
    iconBg: "bg-gradient-to-br from-emerald-500 to-green-600",
    activeBg: "bg-emerald-100 dark:bg-emerald-900/20",
    activeText: "text-emerald-700 dark:text-emerald-400",
    hoverBg: "hover:bg-emerald-50 dark:hover:bg-emerald-900/10",
    dotBg: "bg-emerald-500",
    indicatorBg: "bg-gradient-to-b from-emerald-500 to-green-600",
    submenu: [
      { label: "Vaccines", path: "/admin/vaccines", module: "vaccines" },
      { label: "Vaccine Inventory", path: "/admin/stock-management", module: "vaccine-inventory" },
      { label: "Vaccination Plan", path: "/admin/vaccination-plan", module: "vaccines" },
      { label: "Vaccine Transfer", path: "/admin/vaccine-Transfer", module: "vaccine-transfer" },
      { label: "Supplier Management", path: "/admin/supplier-management", module: "suppliers" },
    ],
  },
  {
    section: "location-management",
    label: "Location Management",
    icon: MapPinIcon,
    iconBg: "bg-gradient-to-br from-amber-500 to-orange-500",
    activeBg: "bg-amber-100 dark:bg-amber-900/20",
    activeText: "text-amber-700 dark:text-amber-400",
    hoverBg: "hover:bg-amber-50 dark:hover:bg-amber-900/10",
    dotBg: "bg-amber-500",
    indicatorBg: "bg-gradient-to-b from-amber-500 to-orange-500",
    submenu: [
      { label: "District", path: "/admin/distric", module: "locations" },
      { label: "Taluka", path: "/admin/taluka", module: "locations" },
      { label: "Village", path: "/admin/village", module: "locations" },
      { label: "Vaccination Center", path: "/admin/centers", module: "centers" },
    ],
  },
  {
    section: "appointments",
    label: "Appointments",
    icon: CalendarIcon,
    iconBg: "bg-gradient-to-br from-violet-500 to-purple-600",
    activeBg: "bg-violet-100 dark:bg-violet-900/20",
    activeText: "text-violet-700 dark:text-violet-400",
    hoverBg: "hover:bg-violet-50 dark:hover:bg-violet-900/10",
    dotBg: "bg-violet-500",
    indicatorBg: "bg-gradient-to-b from-violet-500 to-purple-600",
    submenu: [
      { label: "Appointments", path: "/admin/Appointments", module: "appointments" },
      { label: "Vaccination Records", path: "/admin/vaccination-records", module: "vaccination-records" },
    ],
  },
  {
    section: "medical-staff",
    label: "Medical Staff",
    icon: UserGroupIcon,
    path: "/admin/medical-staff",
    module: "medical-staff",
    iconBg: "bg-gradient-to-br from-pink-500 to-rose-500",
    activeBg: "bg-pink-100 dark:bg-pink-900/20",
    activeText: "text-pink-700 dark:text-pink-400",
    hoverBg: "hover:bg-pink-50 dark:hover:bg-pink-900/10",
    dotBg: "bg-pink-500",
    indicatorBg: "bg-gradient-to-b from-pink-500 to-rose-500",
  },
  {
    section: "Patient Management",
    id: "patient-management",
    label: "Patient Management",
    icon: UsersIcon,
    iconBg: "bg-gradient-to-br from-teal-500 to-cyan-500",
    activeBg: "bg-teal-100 dark:bg-teal-900/20",
    activeText: "text-teal-700 dark:text-teal-400",
    hoverBg: "hover:bg-teal-50 dark:hover:bg-teal-900/10",
    dotBg: "bg-teal-500",
    indicatorBg: "bg-gradient-to-b from-teal-500 to-cyan-500",
    submenu: [
      { label: "Parents", path: "/admin/patients", module: "patients" },
      { label: "Children", path: "/admin/children", module: "children" },
    ],
  },
  /*{
    section: "children",
    label: "Children",
    icon: UsersIcon,
    path: "/admin/children",
    iconBg: "bg-gradient-to-br from-indigo-500 to-blue-600",
    activeBg: "bg-indigo-100 dark:bg-indigo-900/20",
    activeText: "text-indigo-700 dark:text-indigo-400",
    hoverBg: "hover:bg-indigo-50 dark:hover:bg-indigo-900/10",
    dotBg: "bg-indigo-500",
    indicatorBg: "bg-gradient-to-b from-indigo-500 to-blue-600",
  },*/
  {
    section: "users",
    label: "Users",
    icon: UsersIcon,
    module: "users",
    iconBg: "bg-gradient-to-br from-indigo-500 to-blue-600",
    activeBg: "bg-indigo-100 dark:bg-indigo-900/20",
    activeText: "text-indigo-700 dark:text-indigo-400",
    hoverBg: "hover:bg-indigo-50 dark:hover:bg-indigo-900/10",
    dotBg: "bg-indigo-500",
    indicatorBg: "bg-gradient-to-b from-indigo-500 to-blue-600",
    submenu: [
      { label: "Users", path: "/admin/User", module: "users" },
      { label: "Activity Logs", path: "/admin/activity-logs", module: "activity-logs" },
      { label: "Email Notifications", path: "/admin/notification-logs" },
    ],
  },
  {
    section: "reports",
    label: "Reports",
    icon: DocumentChartBarIcon,
    path: "/admin/reports",
    module: "reports",
    iconBg: "bg-gradient-to-br from-cyan-500 to-blue-500",
    activeBg: "bg-cyan-100 dark:bg-cyan-900/20",
    activeText: "text-cyan-700 dark:text-cyan-400",
    hoverBg: "hover:bg-cyan-50 dark:hover:bg-cyan-900/10",
    dotBg: "bg-cyan-500",
    indicatorBg: "bg-gradient-to-b from-cyan-500 to-blue-500",
  },
  {
    section: "settings",
    label: "General Settings",
    icon: Cog6ToothIcon,
    path: "/admin/settings",
    module: "settings",
    iconBg: "bg-gradient-to-br from-slate-500 to-gray-600",
    activeBg: "bg-slate-100 dark:bg-slate-900/20",
    activeText: "text-slate-700 dark:text-slate-300",
    hoverBg: "hover:bg-slate-50 dark:hover:bg-slate-900/10",
    dotBg: "bg-slate-500",
    indicatorBg: "bg-gradient-to-b from-slate-500 to-gray-600",
  },
  {
    section: "roles",
    label: "Roles & Permissions",
    icon: ShieldCheckIcon,
    module: "roles",
    iconBg: "bg-gradient-to-br from-emerald-500 to-green-600",
    activeBg: "bg-emerald-100 dark:bg-emerald-900/20",
    activeText: "text-emerald-700 dark:text-emerald-400",
    hoverBg: "hover:bg-emerald-50 dark:hover:bg-emerald-900/10",
    dotBg: "bg-emerald-500",
    indicatorBg: "bg-gradient-to-b from-emerald-500 to-green-600",
    submenu: [
      { label: "Roles", path: "/admin/roles", module: "roles" },
      { label: "Modules", path: "/admin/modules", module: "roles" },
    ],
  },
];

export default function VaccineSidebar({ collapsed, setCollapsed }) {
  const location = useLocation();
  const darkMode = useSelector(selectDarkMode);
  const { permissions, isSuperAdmin } = useRbac();
  const [isMobile, setIsMobile] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState({});
  const [hoveredItem, setHoveredItem] = useState(null);
  const popoverRef = useRef(null);

  const visibleMenuItems = menuItems
    .map((item) => {
      if (!item.submenu) return item;
      const visibleSubmenu = item.submenu.filter(
        (sub) => !sub.module || canAccessModule(isSuperAdmin, permissions, sub.module)
      );
      return { ...item, submenu: visibleSubmenu };
    })
    .filter((item) => {
      if (item.module) return canAccessModule(isSuperAdmin, permissions, item.module);
      if (item.submenu) return item.submenu.length > 0;
      return true; // dashboard has no module — always visible
    });
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
      if (mobile) setCollapsed(true);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [setCollapsed]);

  // Close expanded menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      // Close collapsed popover when clicking outside
      if (collapsed) {
        const clickedOnPopover = popoverRef.current?.contains(e.target);
        const clickedOnMenuButton = e.target.closest("[data-section]");

        if (!clickedOnPopover && !clickedOnMenuButton) {
          setExpandedMenus({});
        }
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [collapsed]);

  // Close sidebar on mobile when route changes
  useEffect(() => {
    if (isMobile && !collapsed) {
      setCollapsed(true);
    }
    // Close expanded menus when route changes
    setExpandedMenus({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Auto-expand menu if current path is in submenu
  useEffect(() => {
    visibleMenuItems.forEach((item) => {
      if (item.submenu?.some((sub) => location.pathname === sub.path)) {
        setExpandedMenus((prev) => ({ ...prev, [item.section]: true }));
      }
    });
  }, [location.pathname]);

  const toggleSubmenu = (section) =>
    setExpandedMenus((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));

  // Check if path is active (supports sub-routes like /admin/village/add, /admin/village/edit/:id)
  const isActiveItem = (path) => {
    const currentPath = location.pathname.toLowerCase();
    const menuPath = path.toLowerCase();
    // Exact match or starts with the path (for sub-routes)
    return currentPath === menuPath || currentPath.startsWith(menuPath + "/");
  };

  const isSubmenuActive = (submenu) => submenu?.some((item) => isActiveItem(item.path));

  // Handle link click - close sidebar on mobile
  const handleLinkClick = () => {
    if (isMobile) {
      setCollapsed(true);
    }
    setExpandedMenus({});
  };

  return (
    <>
      {/* Mobile overlay */}
      {isMobile && !collapsed && <div onClick={() => setCollapsed(true)} className="fixed inset-0 bg-black/40 z-30 backdrop-blur-sm transition-opacity duration-300" />}

      {/* Sidebar */}
      <aside
        className={`
          fixed left-0 top-0 h-full z-40
          transition-all duration-300 ease-out
          ${darkMode ? "bg-gray-900 border-gray-800" : "bg-white border-gray-200"}
          border-r shadow-xl
          ${collapsed ? "w-[76px]" : "w-[280px]"}
          ${isMobile ? (collapsed ? "-translate-x-full" : "translate-x-0 w-[280px]") : ""}
        `}
      >
        <div className="flex flex-col h-full">
          {/* Close button for mobile */}
          {isMobile && !collapsed && (
            <button
              onClick={() => setCollapsed(true)}
              className={`absolute top-4 right-4 p-2 rounded-full z-50 transition-all duration-200
                ${darkMode ? "hover:bg-gray-700 text-gray-400 hover:text-white" : "hover:bg-gray-100 text-gray-500 hover:text-gray-700"}
              `}
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          )}

          {/* Logo */}
          <div className={`px-4 py-5 border-b ${darkMode ? "border-gray-800" : "border-gray-100"}`}>
            <div className="flex items-center justify-center">
              <img src={collapsed ? vaxicaresmall : vaxicarefull} alt="Vaxicare" className={`transition-all duration-300 ${collapsed ? "h-10" : "h-12"}`} />
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto py-4 px-3">
            <ul className="space-y-1.5">
              {visibleMenuItems.map((item, index) => (
                <li key={item.section} data-section={item.section} className="animate-fade-in" style={{ animationDelay: `${index * 30}ms` }} onMouseEnter={() => setHoveredItem(item.section)} onMouseLeave={() => setHoveredItem(null)}>
                  {item.submenu ? (
                    <div className="relative">
                      {/* Parent menu button */}
                      <button
                        onClick={() => toggleSubmenu(item.section)}
                        className={`
                          w-full flex items-center gap-3 p-3 rounded-xl
                          transition-all duration-200 group
                          ${isSubmenuActive(item.submenu) ? `${item.activeBg} ${item.activeText} font-medium` : darkMode ? `text-gray-300 hover:bg-gray-800` : `text-gray-600 hover:bg-gray-50`}
                        `}
                      >
                        <div
                          className={`
                          p-2.5 rounded-xl ${item.iconBg}
                          text-white shadow-lg
                          transition-transform duration-200 group-hover:scale-110
                        `}
                        >
                          <item.icon className="w-5 h-5" />
                        </div>

                        {!collapsed && (
                          <>
                            <span className="flex-1 text-left text-sm font-medium truncate">{item.label}</span>
                            <ChevronDownIcon
                              className={`w-4 h-4 transition-transform duration-200 flex-shrink-0
                                ${expandedMenus[item.section] ? "rotate-180" : ""}
                              `}
                            />
                          </>
                        )}
                      </button>

                      {/* Submenu - Expanded view */}
                      {!collapsed && (
                        <div
                          className={`
                            overflow-hidden transition-all duration-300 ease-out
                            ${expandedMenus[item.section] ? "max-h-96 opacity-100 mt-1" : "max-h-0 opacity-0"}
                          `}
                        >
                          <ul className={`ml-5 pl-4 space-y-1 border-l-2 ${darkMode ? "border-gray-700" : "border-gray-200"}`}>
                            {item.submenu.map((subitem, subIndex) => (
                              <li key={subitem.label} className={`transition-all duration-200 ${expandedMenus[item.section] ? "translate-x-0 opacity-100" : "-translate-x-2 opacity-0"}`} style={{ transitionDelay: `${subIndex * 40}ms` }}>
                                <Link
                                  to={subitem.path}
                                  onClick={handleLinkClick}
                                  className={`
                                    flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm
                                    transition-all duration-200 hover:translate-x-1
                                    ${isActiveItem(subitem.path) ? `${item.activeBg} ${item.activeText} font-medium` : darkMode ? "text-gray-400 hover:text-white hover:bg-gray-800" : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"}
                                  `}
                                >
                                  <span
                                    className={`
                                    w-2 h-2 rounded-full transition-all duration-200 flex-shrink-0
                                    ${isActiveItem(subitem.path) ? `${item.dotBg} scale-125` : darkMode ? "bg-gray-600" : "bg-gray-300"}
                                  `}
                                  />
                                  <span className="truncate">{subitem.label}</span>
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Submenu - Collapsed popover */}
                      {collapsed && expandedMenus[item.section] && (
                        <div
                          ref={popoverRef}
                          className={`
                            fixed left-[76px] rounded-xl shadow-2xl border py-2 min-w-[200px] z-50 animate-scale-in
                            ${darkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"}
                          `}
                          style={{
                            top: document.querySelector(`[data-section="${item.section}"]`)?.getBoundingClientRect().top,
                          }}
                        >
                          <div className={`px-4 py-2 text-xs font-semibold ${item.activeText} border-b ${darkMode ? "border-gray-700" : "border-gray-100"}`}>{item.label}</div>
                          {item.submenu.map((subitem) => (
                            <Link
                              key={subitem.label}
                              to={subitem.path}
                              onClick={handleLinkClick}
                              className={`
                                flex items-center gap-3 px-4 py-2.5 text-sm
                                transition-all duration-200 hover:translate-x-1
                                ${isActiveItem(subitem.path) ? `${item.activeBg} ${item.activeText} font-medium` : darkMode ? "text-gray-300 hover:bg-gray-700" : "text-gray-600 hover:bg-gray-50"}
                              `}
                            >
                              <span className={`w-2 h-2 rounded-full ${item.dotBg}`} />
                              {subitem.label}
                            </Link>
                          ))}
                        </div>
                      )}

                      {/* Tooltip for collapsed */}
                      {collapsed && !expandedMenus[item.section] && hoveredItem === item.section && (
                        <div
                          className={`
                          fixed left-[76px] ml-2 px-3 py-2 rounded-lg shadow-lg z-50 whitespace-nowrap text-sm font-medium
                          ${darkMode ? "bg-gray-700 text-white" : "bg-gray-900 text-white"}
                        `}
                        >
                          {item.label}
                          <div className={`absolute left-0 top-1/2 -translate-x-1 -translate-y-1/2 border-4 border-transparent ${darkMode ? "border-r-gray-700" : "border-r-gray-900"}`} />
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="relative">
                      <Link
                        to={item.path}
                        onClick={handleLinkClick}
                        className={`
                          flex items-center gap-3 p-3 rounded-xl
                          transition-all duration-200 group
                          ${isActiveItem(item.path) ? `${item.activeBg} ${item.activeText} font-medium` : darkMode ? "text-gray-300 hover:bg-gray-800" : "text-gray-600 hover:bg-gray-50"}
                        `}
                      >
                        <div
                          className={`
                          p-2.5 rounded-xl ${item.iconBg}
                          text-white shadow-lg
                          transition-transform duration-200 group-hover:scale-110
                        `}
                        >
                          <item.icon className="w-5 h-5" />
                        </div>

                        {!collapsed && <span className="text-sm font-medium">{item.label}</span>}

                        {/* Active indicator */}
                        {isActiveItem(item.path) && <div className={`absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 rounded-r-full ${item.indicatorBg}`} />}
                      </Link>

                      {/* Tooltip for collapsed */}
                      {collapsed && hoveredItem === item.section && (
                        <div
                          className={`
                          fixed left-[76px] ml-2 px-3 py-2 rounded-lg shadow-lg z-50 whitespace-nowrap text-sm font-medium
                          ${darkMode ? "bg-gray-700 text-white" : "bg-gray-900 text-white"}
                        `}
                        >
                          {item.label}
                          <div className={`absolute left-0 top-1/2 -translate-x-1 -translate-y-1/2 border-4 border-transparent ${darkMode ? "border-r-gray-700" : "border-r-gray-900"}`} />
                        </div>
                      )}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </nav>

          {/* Footer */}
          {!collapsed && (
            <div className={`p-4 border-t ${darkMode ? "border-gray-800" : "border-gray-100"}`}>
              <div className={`text-xs text-center ${darkMode ? "text-gray-500" : "text-gray-400"}`}>© {new Date().getFullYear()} VaxiCare Admin</div>
            </div>
          )}
        </div>
      </aside>
    </>
  );
}
